import type {IntlShape} from 'react-intl';
import {messages} from '@smartmixin/i18n';
import moment from 'moment-timezone';

/**
 * How much of a report's period has data: the coverage strip under every
 * analysis report's period bar (both apps draw it; this is the shared half).
 *
 * A report computes its figures over the days that exist. Before this, it
 * said nothing about how many that was: a station whose temperature stopped
 * on 6 October read as an 18 °C October. Coverage counts DAYS of the report's
 * lead measurement — per day on a monthly report, per month (with a fill) on
 * an annual one — and says which other measurements differ.
 */

export type CoverageState = 'full' | 'partial' | 'missing' | 'today' | 'future';

export type CoverageBucket = {
  /** Local midnight that opens the bucket (epoch seconds). */
  start: number;
  state: CoverageState;
  /** Share of the bucket's elapsed days with data (0–1). */
  fraction: number;
};

export type CoverageSeries = {
  /** The measurement's name as the line will print it, e.g. "Temperature". */
  label: string;
  /** Timestamps (epoch seconds) of the days that have a value; any time of the day. */
  days: readonly number[];
};

export type PeriodCoverageInput = {
  start: number;
  end: number;
  now: number;
  timezone: string;
  bucket: 'day' | 'month';
  lead: CoverageSeries;
  /** The other measurements the report shows. */
  others?: readonly CoverageSeries[];
};

export type PeriodCoverage = {
  buckets: CoverageBucket[];
  /** Days of the lead measurement with data, up to now. */
  count: number;
  /** Days of the period that have started (today included). */
  total: number;
  /** The period has not ended yet. */
  inProgress: boolean;
  /** Runs of elapsed days with no lead value (today never counts as missing). */
  missingRuns: {start: number; end: number}[];
  bucketKind: 'day' | 'month';
  /** The instant the coverage was computed for. */
  now: number;
  leadLabel: string;
  /** Other measurements with the same day count as the lead one. */
  sameAsLead: string[];
  /** Other measurements whose day count differs. */
  differs: {label: string; count: number}[];
};

const dayKey = (ts: number, tz: string) =>
  moment.tz(ts * 1000, tz).format('YYYY-MM-DD');

/** Every local day of [start, end], DST-safe. */
const periodDays = (start: number, end: number, tz: string) => {
  const out: number[] = [];
  const last = moment.tz(end * 1000, tz).startOf('day');
  for (
    let d = moment.tz(start * 1000, tz).startOf('day');
    d.isSameOrBefore(last);
    d.add(1, 'day')
  ) {
    out.push(d.unix());
  }
  return out;
};

export const getPeriodCoverage = ({
  start,
  end,
  now,
  timezone,
  bucket,
  lead,
  others = [],
}: PeriodCoverageInput): PeriodCoverage => {
  const days = periodDays(start, end, timezone);
  const todayStart = moment
    .tz(now * 1000, timezone)
    .startOf('day')
    .unix();
  const elapsed = days.filter((d) => d <= todayStart);
  const keysOf = (series: CoverageSeries) => {
    const inPeriod = new Set(elapsed.map((d) => dayKey(d, timezone)));
    return new Set(
      series.days
        .map((t) => dayKey(t, timezone))
        .filter((k) => inPeriod.has(k)),
    );
  };
  const leadKeys = keysOf(lead);
  const has = (d: number) => leadKeys.has(dayKey(d, timezone));

  const dayState = (d: number): CoverageState => {
    if (d > todayStart) return 'future';
    if (d === todayStart) return 'today';
    return has(d) ? 'full' : 'missing';
  };

  const missingRuns: {start: number; end: number}[] = [];
  for (const d of elapsed) {
    if (dayState(d) !== 'missing') continue;
    const prev = missingRuns[missingRuns.length - 1];
    const before = moment
      .tz(d * 1000, timezone)
      .subtract(1, 'day')
      .unix();
    if (prev && prev.end === before) {
      prev.end = d;
    } else {
      missingRuns.push({start: d, end: d});
    }
  }

  let buckets: CoverageBucket[];
  if (bucket === 'day') {
    buckets = days.map((d) => ({
      start: d,
      state: dayState(d),
      fraction: has(d) ? 1 : 0,
    }));
  } else {
    const byMonth = new Map<string, number[]>();
    for (const d of days) {
      const k = moment.tz(d * 1000, timezone).format('YYYY-MM');
      byMonth.set(k, [...(byMonth.get(k) ?? []), d]);
    }
    buckets = [...byMonth.values()].map((monthDays) => {
      const past = monthDays.filter((d) => d <= todayStart);
      const fraction = past.length ? past.filter(has).length / past.length : 0;
      let state: CoverageState;
      if (!past.length) state = 'future';
      else if (past.includes(todayStart)) state = 'today';
      else if (fraction === 1) state = 'full';
      else if (fraction === 0) state = 'missing';
      else state = 'partial';
      return {start: monthDays[0], state, fraction};
    });
  }

  const count = leadKeys.size;
  const sameAsLead: string[] = [];
  const differs: {label: string; count: number}[] = [];
  for (const o of others) {
    const c = keysOf(o).size;
    // A measurement with no day at all is one the station does not have.
    if (c === 0) continue;
    if (c === count) sameAsLead.push(o.label);
    else differs.push({label: o.label, count: c});
  }

  return {
    buckets,
    count,
    total: elapsed.length,
    inProgress: todayStart <= days[days.length - 1],
    missingRuns,
    bucketKind: bucket,
    now,
    leadLabel: lead.label,
    sameAsLead,
    differs,
  };
};

export type CoverageFormatters = {
  formatMessage: IntlShape['formatMessage'];
  /** "Temperature, Humidity, and Rain", in the user's language. */
  formatList: (items: string[]) => string;
  /** The time of day of `now`, e.g. "22:00". */
  formatTime: (ts: number) => string;
  /** A run of days: one date when start === end, else a range ("18–21 Jun"). */
  formatDays: (start: number, end: number) => string;
};

/** The most holes the line names; past that it gives the count only. */
const MAX_RUNS = 2;

/** The line beside the strip, and the strip's accessible name. */
export const formatPeriodCoverage = (
  c: PeriodCoverage,
  {formatMessage, formatList, formatTime, formatDays}: CoverageFormatters,
): string => {
  const m = messages.reports;
  const parts: string[] = [];
  if (c.inProgress) {
    parts.push(
      formatMessage(
        c.bucketKind === 'day' ? m.coverageMonthToDate : m.coverageYearToDate,
      ),
    );
  }
  // Today without a reading yet is not a missing day.
  const everyDay = (n: number) =>
    n >= c.total || (c.inProgress && n === c.total - 1);
  const complete = everyDay(c.count) && !c.missingRuns.length;
  if (c.differs.length) {
    const group = (labels: string[], n: number) =>
      everyDay(n)
        ? formatMessage(m.coverageMetricsEveryDay, {
            metrics: formatList(labels),
          })
        : formatMessage(m.coverageMetricsCount, {
            metrics: formatList(labels),
            count: n,
            total: c.total,
          });
    parts.push(group([c.leadLabel, ...c.sameAsLead], c.count));
    for (const d of c.differs) {
      parts.push(
        everyDay(d.count)
          ? formatMessage(m.coverageMetricsEveryDay, {metrics: d.label})
          : formatMessage(m.coverageMetricsCount, {
              metrics: d.label,
              count: d.count,
              total: c.total,
            }),
      );
    }
  } else if (complete && c.inProgress && c.bucketKind === 'day') {
    parts.push(formatMessage(m.coverageTodayUntil, {time: formatTime(c.now)}));
  } else if (complete && !c.inProgress) {
    parts.push(formatMessage(m.coverageAllDays, {total: c.total}));
  } else if (complete) {
    // A year to date with nothing missing: every day so far.
    parts.push(formatMessage(m.coverageAllDays, {total: c.total}));
  } else {
    parts.push(
      formatMessage(m.coverageReadingsCount, {count: c.count, total: c.total}),
    );
  }
  if (
    c.missingRuns.length &&
    c.missingRuns.length <= MAX_RUNS &&
    !c.differs.length
  ) {
    parts.push(
      formatMessage(m.coverageNoReadings, {
        dates: formatList(c.missingRuns.map((r) => formatDays(r.start, r.end))),
      }),
    );
  }
  return parts.join(' · ');
};

/**
 * The first instant the station has history for, when its provider says
 * (Davis, Netatmo, WU…); null when unknown (official stations, a station
 * whose provider never said). The reports' previous-period arrow stops there.
 * Never `historic_start`: that is how far the backfill walk has got, which
 * moves back while a station is still being backfilled.
 */
export const firstDataTimestamp = (station: {
  meta?: {provider?: {first_measure_date?: number; historic_start?: number}};
}): number | null => station.meta?.provider?.first_measure_date ?? null;
